// Destination d'onglet DECLAREE, arbitree tard.
//
// Apres une signature, la destination depend d'un etat qu'on n'a pas encore : la signature acheteur
// d'un BC finalise parfois la validation (BC -> 7) et parfois non (il manque la contre-signature).
// "Notification si dispo, sinon Offre rang X" n'est donc pas decidable au moment du clic.
//
// On n'essaie pas de deviner : on ecrit une LISTE DE PREFERENCES dans ?tab=, et le resolveur choisit
// le premier onglet qui existe ET qui est ouvert, avec les descripteurs frais du render suivant.
// Corollaire utile : ca immunise contre les closures perimees (un `goTo` capture avant le refetch
// juge la destination sur l'etat d'avant, et voit ferme un onglet qui vient de s'ouvrir).

export interface TabLike {
  key: string;
  kind: string;
  disabled?: boolean;
  inactive?: boolean;
}

export const TAB_PARAM_SEPARATOR = ',';

// Une preference = un index numerique, une CLE (`offre-2` : seule facon de viser un rang precis,
// plusieurs onglets partageant le kind `offre`), ou un KIND (liens externes, vues sans rang).
export const buildTabParam = (preferences: string[]): string =>
  preferences.filter(Boolean).join(TAB_PARAM_SEPARATOR);

const isOpen = (tab: TabLike | undefined): boolean =>
  Boolean(tab) && !tab!.disabled && !tab!.inactive;

// Resout UNE preference. Ordre : index, puis cle, puis kind. Un onglet grise n'est jamais retenu :
// c'est la garde qui manquait cote acheteur, ou un lien pouvait ouvrir un onglet interdit.
const resolveOne = (preference: string, descriptors: TabLike[]): number => {
  const trimmed = preference.trim();
  if (!trimmed) return -1;

  const parsed = parseInt(trimmed, 10);
  if (!Number.isNaN(parsed) && parsed >= 0 && parsed < descriptors.length) {
    return isOpen(descriptors[parsed]) ? parsed : -1;
  }

  const byKey = descriptors.findIndex((d) => d.key === trimmed && isOpen(d));
  if (byKey >= 0) return byKey;

  return descriptors.findIndex((d) => d.kind === trimmed && isOpen(d));
};

// Premiere preference atteignable, ou -1 si aucune ne l'est (l'appelant retombe sur son defaut).
export const resolveTabParam = (
  tabParam: string | null,
  descriptors: TabLike[],
): number => {
  if (tabParam === null) return -1;
  for (const preference of tabParam.split(TAB_PARAM_SEPARATOR)) {
    const index = resolveOne(preference, descriptors);
    if (index >= 0) return index;
  }
  return -1;
};

// Rechargement vers une destination. `setSearchParams` est asynchrone : poser l'URL puis appeler
// reload() partirait avec l'ancienne query. `location.replace` fait les deux en une seule operation
// synchrone, et n'ajoute pas d'entree d'historique (le retour arriere reste utilisable).
export const reloadToTab = (preferences: string[]): void => {
  const url = new URL(window.location.href);
  url.searchParams.set('tab', buildTabParam(preferences));
  window.location.replace(url.toString());
};
